import '@plone/volto-slate/blocks/Text/css/editor.css';

import React from 'react';
import { createContent } from '@plone/volto/actions/content/content';
import BlockChooserButton from '@plone/volto/components/manage/BlockChooser/BlockChooserButton';
import { BlockDataForm } from '@plone/volto/components/manage/Form';
import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';
import Image from '@plone/volto/components/theme/Image/Image';
import { validateFileUploadSize } from '@plone/volto/helpers/FormValidation/FormValidation';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import config from '@plone/volto/registry';
import { handleKey } from '@plone/volto-slate/blocks/Text/keyboard';
import MarkdownIntroduction from '@plone/volto-slate/blocks/Text/MarkdownIntroduction';
import TextBlockSchema from '@plone/volto-slate/blocks/Text/schema';
import ShortcutListing from '@plone/volto-slate/blocks/Text/ShortcutListing';
import PersistentSlashMenu from '@plone/volto-slate/blocks/Text/SlashMenu';
import { SlateEditor } from '@plone/volto-slate/editor';
import { serializeNodesToText } from '@plone/volto-slate/editor/render';
import { parseDefaultSelection } from '@plone/volto-slate/utils/selection';
import { deconstructToVoltoBlocks } from '@plone/volto-slate/utils/volto-blocks';
import ImageUploadModal from 'govbr.frontend.policy/components/Widgets/ImageUploadModal';
import { createImageBlock } from 'govbr.frontend.policy/helpers/createImageBlock';
import ReactDOM from 'react-dom';
import Dropzone from 'react-dropzone';
import { useInView } from 'react-intersection-observer';
import { defineMessages, useIntl } from 'react-intl';
import { Dimmer, Loader, Message, Segment } from 'semantic-ui-react';
import { Transforms } from 'slate';

import imageBlockSVG from '@plone/volto/components/manage/Blocks/Image/block-image.svg';

// TODO: refactor dropzone to separate component wrapper

const messages = defineMessages({
  text: {
    id: 'Type text…',
    defaultMessage: 'Type text…',
  },
});

const DEBUG = false;

export const DefaultTextBlockEditor = (props) => {
  const {
    block,
    blocksConfig,
    data,
    detached = false,
    index,
    onChangeBlock,
    onInsertBlock,
    onMutateBlock,
    onSelectBlock,
    pathname,
    properties,
    selected,
    defaultSelection,
    saveSlateBlockSelection,
    allowedBlocks,
    formTitle,
    formDescription,
    navRoot,
    contentType,
  } = props;

  const { slate } = config.settings;
  const { textblockExtensions } = slate;
  const { value } = data;
  const intl = useIntl();

  // const [addNewBlockOpened, setAddNewBlockOpened] = React.useState();
  const [showDropzone, setShowDropzone] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);

  // Modal-first upload de imagens para drag & drop / paste
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalFiles, setModalFiles] = React.useState([]);

  const openImageUploadModal = React.useCallback((files) => {
    const array = Array.isArray(files) ? files : [files];
    if (!array.length) return;
    setModalFiles(array);
    setModalOpen(true);
  }, []);

  const withBlockProperties = React.useCallback(
    (editor) => {
      editor.getBlockProps = () => ({
        ...props,
        openImageUploadModal,
      });
      return editor;
    },
    [props, openImageUploadModal],
  );

  const slateSettings = React.useMemo(
    () => ({
      ...config.settings.slate,
      persistentHelpers: [
        ...config.settings.slate.persistentHelpers,
        PersistentSlashMenu,
      ],
    }),
    [],
  );

  const onDrop = React.useCallback(
    (files) => {
      const validImages = [];
      files.forEach((file) => {
        const [mime] = file.type.split('/');
        if (mime !== 'image') return;
        if (!validateFileUploadSize(file, intl.formatMessage)) return;
        validImages.push(file);
      });

      if (validImages.length) {
        openImageUploadModal(validImages);
      }

      setShowDropzone(false);
    },
    [openImageUploadModal, intl.formatMessage],
  );

  const handleUpdate = React.useCallback(
    (editor) => {
      // defaultSelection is used for things such as "restoring" the selection
      // when joining blocks or moving the selection to block start on block
      // split
      if (defaultSelection) {
        const selection = parseDefaultSelection(editor, defaultSelection);
        if (selection) {
          Transforms.select(editor, selection);
          saveSlateBlockSelection(block, null);
        }
      }
    },
    [defaultSelection, block, saveSlateBlockSelection],
  );

  const onEditorChange = (value, editor) => {
    ReactDOM.unstable_batchedUpdates(() => {
      onChangeBlock(block, {
        ...data,
        value,
        plaintext: serializeNodesToText(value || []),
        // TODO: also add html serialized value
      });
      deconstructToVoltoBlocks(editor);
    });
  };

  // Get editing instructions from block settings or props
  let instructions = data?.instructions?.data || data?.instructions;
  if (!instructions || instructions === '<p><br/></p>') {
    instructions = formDescription;
  }

  const placeholder =
    data.placeholder || formTitle || intl.formatMessage(messages.text);
  const schema = TextBlockSchema(data);

  const disableNewBlocks = data?.disableNewBlocks || detached;
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '0px 0px 200px 0px',
  });

  const handleFocus = React.useCallback(() => {
    if (!selected) {
      onSelectBlock(block);
    }
  }, [onSelectBlock, selected, block]);

  const handleImageModalClose = React.useCallback(() => {
    setModalOpen(false);
    setModalFiles([]);
    setUploading(false);
  }, []);

  // Chamado a cada imagem enviada com sucesso: cria o bloco imediatamente
  const handleImageModalImageUploaded = React.useCallback(
    (imageData) => {
      if (imageData?.['@id']) {
        createImageBlock(imageData, index, props, intl);
      }
    },
    [index, props, intl],
  );

  // Chamado quando TODAS as imagens foram processadas pelo modal
  const handleImageModalComplete = React.useCallback(() => {
    setModalOpen(false);
    setModalFiles([]);
    setUploading(false);
  }, []);

  return (
    <div className="text-slate-editor-inner" ref={ref}>
      <>
        <Dropzone
          disableClick
          onDrop={onDrop}
          className="dropzone"
          onDragOver={() => setShowDropzone(true)}
          onDragLeave={() => setShowDropzone(false)}
        >
          {({ getRootProps, getInputProps }) => {
            return showDropzone ? (
              <div className="drop-indicator">
                {uploading ? (
                  <Dimmer active>
                    <Loader indeterminate>Uploading image</Loader>
                  </Dimmer>
                ) : (
                  <Message>
                    <center>
                      <Image src={imageBlockSVG} alt="" />
                    </center>
                  </Message>
                )}
              </div>
            ) : (
              <>
                <SlateEditor
                  index={index}
                  readOnly={!inView}
                  properties={properties}
                  extensions={textblockExtensions}
                  renderExtensions={[withBlockProperties]}
                  value={value}
                  block={block /* is this needed? */}
                  defaultSelection={defaultSelection}
                  onUpdate={handleUpdate}
                  debug={DEBUG}
                  onFocus={handleFocus}
                  onChange={(value, editor) => onEditorChange(value, editor)}
                  onKeyDown={handleKey}
                  selected={selected}
                  placeholder={placeholder}
                  slateSettings={slateSettings}
                  editableProps={{ 'aria-multiline': 'false' }}
                />
                {DEBUG ? <div>{block}</div> : ''}
              </>
            );
          }}
        </Dropzone>

        {modalOpen && modalFiles.length > 0 && (
          <ImageUploadModal
            open={modalOpen}
            file={modalFiles}
            path={getBaseUrl(pathname)}
            uploadContent={createContent}
            block={block}
            onClose={handleImageModalClose}
            onSuccess={handleImageModalImageUploaded}
            onComplete={handleImageModalComplete}
          />
        )}

        {!config.experimental.addBlockButton.enabled &&
          selected &&
          !data.plaintext?.trim() &&
          !disableNewBlocks && (
            <BlockChooserButton
              data={data}
              block={block}
              onInsertBlock={(id, value) => {
                onSelectBlock(onInsertBlock(id, value));
              }}
              onMutateBlock={onMutateBlock}
              allowedBlocks={allowedBlocks}
              blocksConfig={blocksConfig}
              size="24px"
              properties={properties}
              navRoot={navRoot}
              contentType={contentType}
            />
          )}

        <SidebarPortal selected={selected}>
          <div id="slate-plugin-sidebar"></div>
          {instructions ? (
            <Segment attached>
              <div dangerouslySetInnerHTML={{ __html: instructions }} />
            </Segment>
          ) : (
            <>
              <ShortcutListing />
              <MarkdownIntroduction />
              <BlockDataForm
                block={block}
                schema={schema}
                title={schema.title}
                onChangeBlock={onChangeBlock}
                onChangeField={(id, value) => {
                  onChangeBlock(block, {
                    ...data,
                    [id]: value,
                  });
                }}
                formData={data}
              />
            </>
          )}
        </SidebarPortal>
      </>
    </div>
  );
};

export default DefaultTextBlockEditor;
